import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search, RefreshCw, MessageCircle, Coffee, X as XIcon,
  ExternalLink, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { ProfileNameLink } from "@/components/admin/ProfileNameLink";

// Compact chip used for statuses / flags. Kept text-only so it inherits
// the surrounding card's foreground color and stays readable across
// light + dark themes without extra scoped overrides.
function Chip({ children, tone = "neutral", ...rest }) {
  const toneClass = {
    neutral: "border-border text-[hsl(var(--sb-ink-muted))]",
    green: "border-[hsl(var(--sb-forest))]/60 text-[hsl(var(--sb-forest-2))] bg-[hsl(var(--sb-forest))]/10",
    amber: "border-amber-500/60 text-amber-800 bg-amber-500/10",
    clay: "border-[hsl(var(--sb-clay))]/60 text-[hsl(var(--sb-clay))] bg-[hsl(var(--sb-clay))]/10",
    gold: "border-[hsl(var(--sb-gold))]/60 text-[hsl(var(--sb-gold-2))] bg-[hsl(var(--sb-gold))]/10",
  }[tone] || "border-border";
  return (
    <span
      {...rest}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.14em] ${toneClass}`}
    >
      {children}
    </span>
  );
}

// Label for a single participant/sender. If the user record exists we use
// their display name; otherwise email; otherwise fall back to "Deleted
// user" — a hydration miss almost always means the backing users doc was
// removed. Never leak a raw ObjectId into the moderation UI.
function labelFor(p) {
  if (!p) return "—";
  const name = (p.display_name || "").trim();
  if (name) return name;
  const email = (p.email || "").trim();
  if (email) return email;
  return "Deleted user";
}

function participantsLabel(row) {
  return (row.participants || []).map(labelFor).join(" · ") || "—";
}

function fmtTs(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function statusChip(row) {
  if (row.kind === "coffee_only") {
    const tone = ({
      accepted: "green", proposed: "gold", completed: "green",
      declined: "clay", cancelled: "clay",
    })[row.coffee_status] || "neutral";
    return <Chip tone={tone} data-testid={`admin-conv-status-${row.id}`}>coffee · {row.coffee_status || "—"}</Chip>;
  }
  if (row.kind === "coffee_thread") {
    return (
      <>
        <Chip tone="gold" data-testid={`admin-conv-status-${row.id}`}>coffee → dm</Chip>
        <Chip tone={row.status === "archived" ? "clay" : "green"}>{row.status}</Chip>
      </>
    );
  }
  return <Chip tone={row.status === "archived" ? "clay" : "green"} data-testid={`admin-conv-status-${row.id}`}>direct dm · {row.status}</Chip>;
}

// Composer rendered beneath the transcript inside the admin moderation
// modal. Admin-only (the enclosing panel is already admin-gated), posts
// to /admin/analytics/conversations/{conv_id}/reply, and never attributes
// the message to either participant. The server stamps the entry as
// is_admin_reply=True + admin_display_name="Last Bookmark Admin" so BOTH
// participants and admins see it rendered distinctly in their inbox.
function AdminReplyComposer({ conversationId, threadStatus, onReplied }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const archived = threadStatus === "archived";
  const submit = async (e) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/analytics/conversations/${encodeURIComponent(conversationId)}/reply`,
        { body: trimmed },
      );
      onReplied?.(data);
      setBody("");
      toast.success("Admin reply posted. Both participants were notified.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-4 rounded-xl border-2 border-[hsl(var(--sb-gold))] bg-[hsl(var(--sb-gold))]/8 p-3" data-testid="admin-conv-reply-form">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--sb-ink))] dark:text-[hsl(40_60%_96%)]">
        🛡 Reply as Last Bookmark Admin
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        This reply will appear inside the thread for both participants, clearly labelled as an admin message. Both users will receive a notification.
      </p>
      <textarea
        className="sb-input mt-2 w-full resize-none"
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={archived ? "This thread is archived — admin replies still go through." : "Type your admin message…"}
        maxLength={4000}
        data-testid="admin-conv-reply-input"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] text-muted-foreground">
          {body.length}/4000
        </span>
        <button
          type="submit"
          disabled={sending || !body.trim()}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5 text-sm"
          data-testid="admin-conv-reply-submit"
        >
          {sending ? "Posting…" : "Post admin reply"}
        </button>
      </div>
    </form>
  );
}

// Detail modal — fetches full history from
// /admin/analytics/conversations/{conv_id} and renders a single scrollable
// transcript: coffee-chat invite (if any) at the top, followed by every
// thread message in chronological order.
function ConversationDetailModal({ conversationId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setData(null);
      setError(null);
      try {
        const { data: d } = await api.get(
          `/admin/analytics/conversations/${encodeURIComponent(conversationId)}`
        );
        if (alive) setData(d);
      } catch (err) {
        if (alive) setError(formatApiError(err));
      }
    })();
    return () => { alive = false; };
  }, [conversationId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" data-testid="admin-conv-detail-modal">
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-background p-6 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-sm rounded-full border border-border px-2 py-1 hover:bg-muted"
          data-testid="admin-conv-detail-close"
        >
          <XIcon size={14} />
        </button>

        {error && <div className="text-sm text-[hsl(var(--sb-clay))]">{error}</div>}
        {!data && !error && <div className="text-sm text-muted-foreground">Loading conversation…</div>}

        {data && (
          <>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              Admin moderation — {data.kind.replace("_", " ")}
            </div>
            <div className="mt-1 text-lg font-semibold text-foreground">
              {(() => {
                // Pull the participant list from whichever record has it:
                // threads carry `participants` on the thread object; a
                // stand-alone coffee-chat carries `author`/`reader`. A
                // DIRECT DM has a thread but no coffee_chat, so we must
                // never dereference `coffee_chat` in that branch.
                const src = data.thread?.participants
                  ? data.thread.participants
                  : data.coffee_chat
                    ? [data.coffee_chat.author, data.coffee_chat.reader]
                    : [];
                const people = src.filter(Boolean);
                if (!people.length) return "—";
                return people.map((p, i) => (
                  <span key={p.id || i}>
                    {i > 0 && " · "}
                    <ProfileNameLink user={p} testId={`admin-conv-heading-profile-${p.id}`}>{labelFor(p)}</ProfileNameLink>
                  </span>
                ));
              })()}
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5 items-center text-[11px] text-muted-foreground">
              {data.project ? (
                <span className="inline-flex items-center gap-1">
                  <ExternalLink size={11} />
                  Project: <span className="font-medium text-foreground">{data.project.title || data.project.id}</span>
                </span>
              ) : (
                <span>No linked project</span>
              )}
              <span>·</span>
              <span>{data.message_count} entr{data.message_count === 1 ? "y" : "ies"}</span>
            </div>

            {data.coffee_chat && (
              <div className="mt-3 rounded-lg border border-[hsl(var(--sb-gold))]/50 bg-[hsl(var(--sb-gold))]/10 p-3 text-xs" data-testid="admin-conv-detail-coffee-header">
                <div className="flex items-center gap-2 text-[11px] font-semibold">
                  <Coffee size={12} /> Coffee chat · <span className="uppercase tracking-[0.14em]">{data.coffee_chat.status}</span>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Requested by {labelFor(data.coffee_chat.requester)}
                  {" · "}{fmtTs(data.coffee_chat.created_at)}
                </div>
              </div>
            )}

            <div className="mt-4 space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="admin-conv-detail-transcript">
              {(data.entries || []).map((e) => {
                const isInvite = e.kind === "coffee_invite";
                const isAdminReply = e.kind === "message" && e.is_admin_reply;
                return (
                  <div
                    key={e.id}
                    className={`rounded-lg border p-2.5 text-xs ${
                      isAdminReply
                        ? "border-[hsl(var(--sb-gold))] bg-[hsl(var(--sb-gold))]/15 dark:bg-[hsl(var(--sb-gold))]/25"
                        : isInvite
                          ? "border-[hsl(var(--sb-gold))]/50 bg-[hsl(var(--sb-gold))]/8"
                          : "border-border bg-muted/30"
                    }`}
                    data-testid={`admin-conv-entry-${e.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-[11px] font-semibold text-foreground min-w-0 truncate">
                        {isInvite && <Coffee size={11} className="inline mr-1" />}
                        {isAdminReply ? (
                          <span className="inline-flex items-center gap-1 font-bold uppercase tracking-[0.14em] text-[10px] text-[hsl(var(--sb-ink))] dark:text-[hsl(40_60%_96%)]">
                            🛡 {e.admin_display_name || "Last Bookmark Admin"}
                          </span>
                        ) : (
                          <>
                            <ProfileNameLink user={e.sender} testId={`admin-conv-entry-profile-${e.id}`}>{labelFor(e.sender)}</ProfileNameLink>
                            {e.sender?.is_demo && <Chip tone="clay" style={{ marginLeft: 6 }}>demo</Chip>}
                            {e.sender?.is_private_test && <Chip tone="gold" style={{ marginLeft: 6 }}>pta</Chip>}
                          </>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtTs(e.created_at)}</div>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap break-words text-[11px] text-foreground">{e.body || <span className="italic text-muted-foreground">(empty)</span>}</div>
                  </div>
                );
              })}
              {(data.entries || []).length === 0 && (
                <div className="text-xs text-muted-foreground italic">
                  This conversation has no messages yet — likely a proposed coffee chat that was never accepted.
                </div>
              )}
            </div>
            {data.thread && (
              <AdminReplyComposer
                conversationId={conversationId}
                threadStatus={data.thread.status}
                onReplied={(msg) => {
                  // Append the admin reply to the local transcript so
                  // moderators immediately see their intervention without
                  // closing + reopening the modal.
                  setData((d) => d ? ({
                    ...d,
                    message_count: (d.message_count || 0) + 1,
                    entries: [...(d.entries || []), {
                      kind: "message",
                      id: msg.id,
                      sender: null,
                      body: msg.body,
                      created_at: msg.created_at,
                      is_admin_reply: true,
                      admin_display_name: msg.admin_display_name,
                    }],
                  }) : d);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────
export function AdminConversationsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [hasProject, setHasProject] = useState("all");
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);

  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        q, kind, status, has_project: hasProject,
        limit: String(limit), offset: String(offset),
      });
      const { data } = await api.get(`/admin/analytics/conversations?${params.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setDiag(data.collections || null);
    } catch (err) {
      setError(formatApiError(err));
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, kind, status, hasProject, limit, offset]);

  useEffect(() => { load(); }, [load]);

  // Reset pagination when filters change
  const setFilter = (setter) => (v) => { setter(v); setOffset(0); };

  const statusOptions = useMemo(() => [
    { v: "all", label: "Any status" },
    { v: "active", label: "Active (thread)" },
    { v: "archived", label: "Archived (thread)" },
    { v: "proposed", label: "Proposed (coffee)" },
    { v: "accepted", label: "Accepted (coffee)" },
    { v: "declined", label: "Declined (coffee)" },
    { v: "cancelled", label: "Cancelled (coffee)" },
    { v: "completed", label: "Completed (coffee)" },
  ], []);

  return (
    <div className="mt-6 space-y-4" data-testid="admin-conversations-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            placeholder="Search names, emails, message body, project…"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-border bg-background text-sm"
            data-testid="admin-conv-search"
          />
        </div>
        <select
          value={kind}
          onChange={(e) => setFilter(setKind)(e.target.value)}
          className="rounded-full border border-border bg-background px-3 py-1.5 text-sm"
          data-testid="admin-conv-kind"
        >
          <option value="all">All kinds</option>
          <option value="thread">Threads only</option>
          <option value="coffee_only">Coffee-only invites</option>
        </select>
        <select
          value={status}
          onChange={(e) => setFilter(setStatus)(e.target.value)}
          className="rounded-full border border-border bg-background px-3 py-1.5 text-sm"
          data-testid="admin-conv-status-filter"
        >
          {statusOptions.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select
          value={hasProject}
          onChange={(e) => setFilter(setHasProject)(e.target.value)}
          className="rounded-full border border-border bg-background px-3 py-1.5 text-sm"
          data-testid="admin-conv-project-filter"
        >
          <option value="all">Project link: any</option>
          <option value="yes">Has project</option>
          <option value="no">No project link</option>
        </select>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted"
          data-testid="admin-conv-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {diag && (
        <div className="text-[11px] text-muted-foreground" data-testid="admin-conv-diag">
          Indexed {diag.threads} thread{diag.threads === 1 ? "" : "s"} + {diag.coffee_chats} coffee chat{diag.coffee_chats === 1 ? "" : "s"} → {total} conversation{total === 1 ? "" : "s"} shown ({diag.folded_chats} chat{diag.folded_chats === 1 ? "" : "s"} folded into their threads).
        </div>
      )}

      {loading && <div className="text-sm text-muted-foreground">Loading conversations…</div>}
      {error && <div className="text-sm text-[hsl(var(--sb-clay))]">{error}</div>}

      {!loading && rows.length === 0 && (
        <div className="text-sm text-muted-foreground italic" data-testid="admin-conv-empty">
          No conversations match these filters.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r) => {
          const preview = r.latest_preview;
          const snippet = (preview?.body || "").length > 180
            ? `${preview.body.slice(0, 180)}…`
            : (preview?.body || "");
          const senderLabel = labelFor(preview?.sender);
          return (
            <button
              key={r.id}
              onClick={() => setSelected(r.id)}
              className="w-full text-left rounded-lg border border-border p-3 hover:bg-muted/40 transition-colors"
              data-testid={`admin-conv-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.kind === "coffee_only" ? <Coffee size={14} className="text-[hsl(var(--sb-gold-2))]" /> : <MessageCircle size={14} className="text-[hsl(var(--sb-forest-2))]" />}
                    <span className="font-semibold text-sm text-foreground truncate">{participantsLabel(r)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    {statusChip(r)}
                    {r.project && (
                      <Chip tone="neutral" data-testid={`admin-conv-project-${r.id}`}>
                        <ExternalLink size={9} /> {r.project.title || r.project.id}
                      </Chip>
                    )}
                    {!r.project && (
                      <Chip tone="neutral">no project</Chip>
                    )}
                    {r.matched_by_body && (
                      <Chip tone="amber" data-testid={`admin-conv-match-${r.id}`}>match in body</Chip>
                    )}
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground whitespace-nowrap text-right">
                  {r.message_count} msg{r.message_count === 1 ? "" : "s"}
                  <div>{fmtTs(r.last_activity_at)}</div>
                </div>
              </div>
              {preview && (
                <div className="mt-2 rounded bg-muted/40 border border-border/60 p-2 text-[11px]" data-testid={`admin-conv-preview-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-medium truncate">{senderLabel}</span>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtTs(preview.created_at)}</span>
                  </div>
                  <div className="mt-0.5 whitespace-pre-wrap break-words text-[11px]">{snippet || <span className="italic text-muted-foreground">(empty)</span>}</div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {total > limit && (
        <div className="flex items-center justify-between gap-2 pt-2">
          <div className="text-[11px] text-muted-foreground">
            Showing {offset + 1}–{Math.min(offset + rows.length, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-40"
              data-testid="admin-conv-prev"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
              className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-40"
              data-testid="admin-conv-next"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {selected && (
        <ConversationDetailModal
          conversationId={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

export default AdminConversationsPanel;
